import * as React from "react";

import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon, type IconName } from "../../components/ui/icon";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import { useOutsideClick } from "../../hooks/use-outside-click";
import type { Omittable } from "../../lib/props";
import {
  InlineEditingCell,
  InlineRowActions,
  InlineValueCell,
  InlineValueTrigger,
  type InlineRowActionsVisibility,
} from "./inline-row";

/**
 * The orchestrated inline-edit VALUE cell: one component owning every
 * behavior a table/row cell needs to view and edit a single value —
 * rest-display/editor switching, touch tap-to-reveal, outside-click
 * dismissal with invalid-draft discard, host-highlight lifecycle, the
 * hover-revealed value-actions row, overlay open/close reporting,
 * validation gating (an invalid commit vetoes deactivation; clicking away
 * is the give-up gesture), and a baseline snapshot behind `reset`.
 *
 * Everything value-specific is injected: the display node, the editor (one
 * of three composition kinds below), the action descriptors, and the
 * `validate` callback. The cell never interprets `value` — it only
 * snapshots it for `reset` and passes commits through.
 *
 * Editor kinds:
 * - `inline`   — the host drives `isActive`; at rest the cell is a button
 *                that requests activation, while active the injected editor
 *                renders inside `InlineEditingCell` and commits/deactivates
 *                through its api.
 * - `overlay`  — the editor is a floating surface (popover, drawer, picker)
 *                anchored on the cell; open state lives here and reports
 *                through `onOverlayChange`. The render callback composes the
 *                overlay around a cell body built by `cellWithTriggerPart`
 *                (a Base UI trigger part) or `cellWithTrigger` (a plain
 *                button). `defer` mounts the overlay component only while
 *                open — for editors that are expensive at rest.
 * - `content`  — the value edits in place (a toggle, a tile grid): the
 *                injected content replaces the display with no activation
 *                step.
 */

const DEFAULT_CELL_HEIGHT = 44;

/** Width of one action button slot in the value-actions row. */
const ACTION_SLOT_WIDTH = 28;

// The actions pill floats over the value text, so it needs an opaque fill —
// and it is only ever visible while its cell shows hover/selected/editing
// paint, so the fill must be THAT exact color, not a flat token that merely
// approximates one of its states. The color is a composite the pill rebuilds
// layer by layer from the variables every host publishes:
//
//   --table-fill   the opaque fill under the rows. Set by DataTable's scroll
//                  container; inline rows sit directly on the page, so the
//                  fallback is `--background`.
//   --row-wash     the row's current wash. A DataTable row maintains it for
//                  its pinned cells; `InlineRow` exports its selected wash
//                  under the same name.
//   --cell-wash    the cell's own wash on top of the row —
//                  `InlineValueCell` (hover) and `InlineEditingCell`
//                  (active) both paint FROM this variable, so the pill can
//                  never disagree with the cell it floats in.
//
// Stacked cell-over-row-over-fill, this is the same three-layer recipe the
// table's pinned cells use to punch through scrolling content.
const ACTIONS_PILL_CLASS =
  "bg-[color:var(--table-fill,var(--background))] bg-[image:linear-gradient(var(--cell-wash,transparent),var(--cell-wash,transparent)),linear-gradient(var(--row-wash,transparent),var(--row-wash,transparent))] drop-shadow-sm";

function blurActiveElement(): void {
  if (typeof document === "undefined") return;
  (document.activeElement as HTMLElement | null)?.blur?.();
}

/**
 * Touch tap-to-reveal gate. First tap on a value cell reveals the action
 * buttons; second tap performs the primary action. Empty values skip the
 * reveal step — there are no value-actions to show, so the first tap goes
 * straight through to the activation handler.
 *
 * Gated on touch capability (not viewport size) so tablets at desktop-width
 * breakpoints still get the tap-to-reveal flow.
 *
 * Invariant: `onOverlayChange(true)` MUST fire in the same render as
 * `setRevealed(true)`. The host-highlight dismiss effect watches
 * `highlighted` and will otherwise see the stale `false` and immediately
 * cancel the reveal.
 */
function useRevealOnTouchGate({
  isTouch,
  hasValue,
  onOverlayChange,
}: Omittable<{
  isTouch: boolean;
  hasValue: boolean;
  onOverlayChange?: (open: boolean) => void;
}>) {
  const [revealed, setRevealed] = React.useState(false);
  const absorb = React.useCallback(
    (next: boolean): boolean => {
      if (next && isTouch && hasValue && !revealed) {
        setRevealed(true);
        onOverlayChange?.(true);
        return true;
      }
      return false;
    },
    [isTouch, hasValue, onOverlayChange, revealed],
  );
  return { revealed, setRevealed, absorb };
}

/** Imperative surface handed to action descriptors and host chrome. */
export type EditableValueCellApi = {
  /** Validate-and-commit. `undefined` means "remove the value" and always
   *  passes (a removal cannot be invalid). */
  commit: (value: unknown) => void;
  /** Deactivation request; vetoed while the last commit was invalid. */
  deactivate: () => void;
  /** Write a known-good value WITHOUT validating, close any overlay, exit
   *  edit mode and drop focus — the shared shape of "clear"-style
   *  affordances that restore a constructed-safe value. */
  overwrite: (value: unknown) => void;
  /** Revert to the value snapshotted when edit mode was entered. */
  reset: () => void;
};

export type EditableValueCellAction = Omittable<{
  key: string;
  icon: IconName;
  /** Accessible label ("Copy email", "Clear value"). */
  label: string;
  onSelect: (cell: EditableValueCellApi) => void;
}>;

export type EditableValueCellInlineEditorApi = {
  commit: (value: unknown) => void;
  deactivate: () => void;
  /** The last commit attempt was rejected by `validate`. */
  invalid: boolean;
};

export type EditableValueCellOverlayApi = {
  open: boolean;
  /**
   * Open/close request routed through the touch reveal gate. Returns false
   * when the gate absorbed an open request to reveal the value actions
   * instead — a render adding its own open-edge side effects (draft
   * snapshots) must skip them on an absorbed request.
   */
  onOpenChange: (open: boolean) => boolean;
  close: () => void;
  commit: (value: unknown) => void;
  /** Deactivation request; vetoed while the last commit was invalid. */
  deactivate: () => void;
  /**
   * The standard rest cell wearing the given overlay trigger part (a
   * `PopoverTrigger`/`SelectTrigger`-style element that accepts `render`).
   * The part is cloned with the cell's own trigger button as its `render`,
   * display node inside — so every overlay editor's rest state is
   * byte-identical chrome.
   */
  cellWithTriggerPart: (
    part: React.ReactElement<{ render?: React.ReactElement }>,
  ) => React.ReactNode;
  /** The standard rest cell as a plain button that opens the overlay on
   *  click. `onClick` replaces the default open request, for renders whose
   *  open edge carries side effects — route back through `onOpenChange`. */
  cellWithTrigger: (onClick?: () => void) => React.ReactNode;
};

export type EditableValueCellContentApi = {
  commit: (value: unknown) => void;
};

export type EditableValueCellEditor =
  | Omittable<{
      kind: "inline";
      render: (api: EditableValueCellInlineEditorApi) => React.ReactNode;
    }>
  | Omittable<{
      kind: "overlay";
      render: (api: EditableValueCellOverlayApi) => React.ReactNode;
      /**
       * Mount the overlay component only while it is open; at rest the cell
       * renders `cellWithTrigger` itself. For editors that carry real cost at
       * rest (a per-instance query, a heavy pool) in cells rendered once per
       * visible row. Trade-off: closing unmounts immediately, so the
       * overlay's exit animation is skipped.
       */
      defer?: boolean;
    }>
  | Omittable<{
      kind: "content";
      render: (api: EditableValueCellContentApi) => React.ReactNode;
    }>;

export type EditableValueCellProps = Omittable<{
  /** Opaque to the cell — only snapshotted for `reset`. */
  value: unknown;
  /** Rest display node (also the read-only body). */
  display: React.ReactNode;
  /** Drives the touch reveal gate: empty values skip straight to editing. */
  hasValue: boolean;
  editor?: EditableValueCellEditor;
  /** Accessible label for the rest trigger ("Edit Phone"). */
  triggerLabel?: string;
  /** Cell height in px; with `grow` it is a minimum instead. */
  height?: number;
  /** Content-sized cell (wrapping grids): `height` becomes `minHeight`. */
  grow?: boolean;
  /**
   * Extend the cell 0.5rem past its container's end edge, into the host
   * row's own padding — the inline-ROW look, where the hover outline reads
   * wider than the value column. Pass false inside a hard-clipped container
   * (a table cell), where the overhang would clip the outline's end corners
   * square.
   */
  bleedEnd?: boolean;
  /** Display + actions only — no trigger, no editor, no activation. */
  readOnly?: boolean;
  actions?: readonly EditableValueCellAction[];
  /** Force-hide the value actions (e.g. while the host's row menu is up). */
  suppressActions?: boolean;
  /** Returns an error message to reject the commit, null to accept. */
  validate?: (value: unknown) => string | null;
  onCommit: (value: unknown) => void;
  /** Fires on every rejected commit attempt (host raises its feedback). */
  onInvalidCommit?: (message: string) => void;
  /** Invalid-state edges, for hosts that gate switching between cells. */
  onInvalidChange?: (invalid: boolean) => void;
  /** Validation message entering/leaving — feeds the host's label. */
  onValidationErrorChange?: (message: string | null) => void;
  /** Host-owned edit mode (inline editors only). */
  isActive?: boolean;
  onActivate?: () => void;
  onDeactivate?: () => void;
  /** Host-tracked highlight: while false, overlays and touch reveal dismiss. */
  highlighted?: boolean;
  /** Any overlay (picker, popover, touch reveal) opened or closed. */
  onOverlayChange?: (open: boolean) => void;
  /** Imperative hook for "Reset value"-style host chrome. */
  apiRef?: React.RefObject<EditableValueCellApi | null>;
}>;

export function EditableValueCell({
  value,
  display,
  hasValue,
  editor,
  triggerLabel = "Edit value",
  height = DEFAULT_CELL_HEIGHT,
  grow = false,
  bleedEnd = true,
  readOnly = false,
  actions,
  suppressActions = false,
  validate,
  onCommit,
  onInvalidCommit,
  onInvalidChange,
  onValidationErrorChange,
  isActive = false,
  onActivate,
  onDeactivate: onDeactivateRaw,
  highlighted = false,
  onOverlayChange,
  apiRef,
}: EditableValueCellProps): React.ReactElement {
  // Tap-to-reveal only when the primary input has no hover (true
  // tablets/phones). Touchscreen laptops report a coarse pointer but still
  // hover, so the hook excludes them.
  const isTouch = useIsTouchDevice();
  const [overlayOpen, setOverlayOpen] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );
  // Synchronous gate for an editor's same-tick `commit` + `deactivate` pair.
  // Set in `commit` so the deactivation wrapper can veto before the
  // corresponding state update has flushed.
  const invalidRef = React.useRef(false);
  const cellRef = React.useRef<HTMLDivElement | null>(null);

  // Snapshot of the value at the moment edit mode was entered, so host
  // chrome can offer "reset" back to the last committed state.
  const baselineRef = React.useRef<unknown>(value);
  const wasActiveRef = React.useRef(isActive);
  React.useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      baselineRef.current = value;
    }
    wasActiveRef.current = isActive;
    // value intentionally omitted — the snapshot is taken at the entry edge,
    // not on every value change while active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const deactivate = React.useCallback(() => {
    if (invalidRef.current) return;
    onDeactivateRaw?.();
  }, [onDeactivateRaw]);

  const commit = React.useCallback(
    (newValue: unknown) => {
      if (newValue === undefined) {
        invalidRef.current = false;
        onInvalidChange?.(false);
        setValidationError(null);
        onCommit(undefined);
        return;
      }
      const message = validate?.(newValue) ?? null;
      if (message !== null) {
        invalidRef.current = true;
        onInvalidChange?.(true);
        setValidationError(message);
        onInvalidCommit?.(message);
        return;
      }
      invalidRef.current = false;
      onInvalidChange?.(false);
      setValidationError(null);
      onCommit(newValue);
    },
    [onCommit, onInvalidChange, onInvalidCommit, validate],
  );

  // The host renders the label, so the error surfaces there — report edges.
  const onValidationErrorChangeRef = React.useRef(onValidationErrorChange);
  onValidationErrorChangeRef.current = onValidationErrorChange;
  React.useEffect(() => {
    onValidationErrorChangeRef.current?.(validationError);
  }, [validationError]);

  const { revealed, setRevealed, absorb } = useRevealOnTouchGate({
    isTouch,
    hasValue,
    onOverlayChange,
  });

  const setOverlay = React.useCallback(
    (next: boolean) => {
      setOverlayOpen(next);
      onOverlayChange?.(next);
    },
    [onOverlayChange],
  );

  // Host moved the highlight elsewhere — dismiss without reporting (the
  // host already knows; it dropped the highlight).
  React.useEffect(() => {
    if (highlighted) return;
    setOverlayOpen(false);
    setRevealed(false);
  }, [highlighted, setRevealed]);

  // Clear stale validation state when the cell exits edit mode or the value
  // changes externally — otherwise the editor reverts on blur but the error
  // label sticks, and a stale invalidRef would veto the NEXT edit session's
  // first deactivation. The host is notified so its own invalid gate
  // releases even when deactivation happened outside the cell's control.
  const onInvalidChangeRef = React.useRef(onInvalidChange);
  onInvalidChangeRef.current = onInvalidChange;
  React.useEffect(() => {
    if (!isActive) {
      if (invalidRef.current) {
        invalidRef.current = false;
        onInvalidChangeRef.current?.(false);
      }
      setValidationError(null);
    }
  }, [isActive, value]);

  // Outside-click dismissal — touch reveal and inline-active editing.
  // Must also clear the host-tracked highlight, otherwise the cell stays
  // visually selected until another one is tapped.
  const dismissReveal = React.useCallback(() => {
    setRevealed(false);
    onOverlayChange?.(false);
  }, [onOverlayChange, setRevealed]);
  const handleCellOutside = React.useCallback(() => {
    if (isActive) {
      // An invalid draft never committed (`commit` withheld the write), so
      // clicking away is the give-up gesture: discard the draft and exit.
      // Without this branch the invalid veto in `deactivate` would lock the
      // cell in edit mode with no way out on hosts that render no reset
      // affordance.
      if (invalidRef.current) {
        invalidRef.current = false;
        onInvalidChange?.(false);
        setValidationError(null);
        onDeactivateRaw?.();
      } else {
        deactivate();
      }
    }
    if (isTouch && revealed) dismissReveal();
  }, [
    deactivate,
    dismissReveal,
    isActive,
    isTouch,
    onDeactivateRaw,
    onInvalidChange,
    revealed,
  ]);
  useOutsideClick({
    ref: cellRef,
    active: isActive || (isTouch && revealed),
    onOutside: handleCellOutside,
  });

  const overwrite = React.useCallback(
    (newValue: unknown) => {
      // Blur FIRST: a still-focused editor flushes its blur-commit now, so
      // the write below lands last instead of being clobbered by the draft.
      blurActiveElement();
      invalidRef.current = false;
      onInvalidChange?.(false);
      setValidationError(null);
      setOverlay(false);
      onCommit(newValue);
      onDeactivateRaw?.();
    },
    [onCommit, onDeactivateRaw, onInvalidChange, setOverlay],
  );

  const reset = React.useCallback(() => {
    overwrite(baselineRef.current);
  }, [overwrite]);

  const cellApi = React.useMemo<EditableValueCellApi>(
    () => ({ commit, deactivate, overwrite, reset }),
    [commit, deactivate, overwrite, reset],
  );

  React.useEffect(() => {
    if (!apiRef) return;
    apiRef.current = cellApi;
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, cellApi]);

  const actionsVisibility: InlineRowActionsVisibility = (() => {
    if (
      !actions ||
      actions.length === 0 ||
      isActive ||
      overlayOpen ||
      suppressActions
    )
      return "hidden";
    if (revealed) return "always";
    return "valueHoverFocus";
  })();

  const actionsNode =
    actions && actions.length > 0 ? (
      <Box position="absolute" right={4} top={0} bottom={0} justify="center">
        <InlineRowActions
          width={actions.length * ACTION_SLOT_WIDTH}
          height={24}
          rounded="sm"
          className={ACTIONS_PILL_CLASS}
          visibility={actionsVisibility}
        >
          {actions.map((action) => (
            <Button
              key={action.key}
              variant="outline"
              size="icon-xs"
              aria-label={action.label}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                action.onSelect(cellApi);
              }}
            >
              <Icon name={action.icon} size="md" />
            </Button>
          ))}
        </InlineRowActions>
      </Box>
    ) : null;

  const sizing = grow ? { minHeight: height } : { height };

  const restCell = (body: React.ReactNode) => (
    <InlineValueCell {...sizing} bleedEnd={bleedEnd}>
      {body}
      {actionsNode}
    </InlineValueCell>
  );

  const renderBody = (): React.ReactNode => {
    // Read-only short-circuit: plain display, no trigger, no editor, no
    // activation — but still inside `InlineValueCell` so hover-revealed
    // actions (a copy button) keep working.
    if (readOnly || !editor) return restCell(display);

    if (editor.kind === "content") {
      return restCell(editor.render({ commit }));
    }

    if (editor.kind === "inline") {
      if (isActive) {
        return (
          <InlineEditingCell {...sizing} bleedEnd={bleedEnd}>
            {editor.render({
              commit,
              deactivate,
              invalid: validationError !== null,
            })}
          </InlineEditingCell>
        );
      }
      return restCell(
        <InlineValueTrigger
          aria-label={triggerLabel}
          onClick={() => {
            if (absorb(true)) return;
            onActivate?.();
          }}
        >
          {display}
        </InlineValueTrigger>,
      );
    }

    const requestOpenChange = (next: boolean): boolean => {
      if (absorb(next)) return false;
      setOverlay(next);
      return true;
    };
    const cellWithTrigger = (onClick?: () => void) =>
      restCell(
        <InlineValueTrigger
          aria-label={triggerLabel}
          onClick={onClick ?? (() => requestOpenChange(true))}
        >
          {display}
        </InlineValueTrigger>,
      );
    if (editor.defer && !overlayOpen) return cellWithTrigger();
    return editor.render({
      open: overlayOpen,
      onOpenChange: requestOpenChange,
      close: () => setOverlay(false),
      commit,
      deactivate,
      cellWithTriggerPart: (part) =>
        restCell(
          React.cloneElement(part, {
            render: (
              <InlineValueTrigger aria-label={triggerLabel}>
                {display}
              </InlineValueTrigger>
            ),
          }),
        ),
      cellWithTrigger,
    });
  };

  // The outside-click boundary for deactivation/dismissal. `fill="flex"` +
  // stretch so the InlineValueCell inside sizes exactly as it does against a
  // bare flex host.
  return (
    <Box
      ref={cellRef}
      fill="flex"
      minWidth={0}
      direction="row"
      align="stretch"
    >
      {renderBody()}
    </Box>
  );
}
